import { useCallback, useEffect, useMemo, useState } from "react";

export const openViews = [
  "Login",
  "Signup",
  "SignupWithCode",
  "GuestForm",
  "QuickScoreResults",
  "QuickScoreResultsDirect",
  "InvitationCode",
];

export function navigate(path) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function useLocation() {
  const read = () => ({
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  });
  const [location, setLocation] = useState(read);

  useEffect(() => {
    const handler = () => setLocation(read());
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  return location;
}

export function useNavigate() {
  return useCallback((path) => navigate(path), []);
}

export function useRouteMatch() {
  const location = useLocation();
  return useMemo(() => matchRoute(location.pathname, location.search), [location.pathname, location.search]);
}

function normalizePathname(pathname) {
  const raw = typeof pathname === "string" ? pathname : "/";
  if (raw === "/") return "/";

  const trimmed = raw.replace(/\/+$/, "");
  if (!trimmed) return "/";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

function safeDecodeURIComponent(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function getLastPathSegment(pathname) {
  const segments = normalizePathname(pathname).split("/").filter(Boolean);
  return segments.length > 0 ? segments[segments.length - 1] : "";
}

export function matchRoute(pathname, search = "") {
  const normalizedPathname = normalizePathname(pathname);
  const query = Object.fromEntries(new URLSearchParams(search).entries());

  if (normalizedPathname === "/login") return { name: "Login", view: "login", params: {}, query };
  if (normalizedPathname === "/signup") return { name: "Signup", view: "signup", params: {}, query };
  if (normalizedPathname.startsWith("/signupcode/")) {
    return {
      name: "SignupWithCode",
      view: "signup",
      params: { code: safeDecodeURIComponent(getLastPathSegment(normalizedPathname)) },
      query,
    };
  }
  if (normalizedPathname === "/invitation-code") return { name: "InvitationCode", view: "invitation-code", params: {}, query };
  if (normalizedPathname.startsWith("/guestForm/")) {
    return {
      name: "GuestForm",
      view: "guest-form",
      params: { id: safeDecodeURIComponent(getLastPathSegment(normalizedPathname)) },
      query,
    };
  }
  if (normalizedPathname.startsWith("/q/")) {
    return {
      name: "QuickScoreResultsDirect",
      view: "quick-score-results",
      params: { reference: safeDecodeURIComponent(getLastPathSegment(normalizedPathname)) },
      query,
    };
  }
  if (normalizedPathname === "/quickscoreresults") {
    return { name: "QuickScoreResults", view: "quick-score-results", params: {}, query };
  }

  const guarded = {
    "/": ["Dashboard", "dashboard"],
    "/home": ["HomeDefault", "dashboard"],
    "/account": ["Account", "account"],
    "/listings": ["Listings", "listings"],
    "/contact": ["Contact", "contact"],
    "/topup": ["TopUp", "topup"],
    "/faq": ["FAQ", "faq"],
    "/gig": ["GIG", "gig"],
    "/password": ["Password", "password"],
    "/email": ["Email", "email"],
    "/userinfo": ["UserInfo", "userinfo"],
    "/checkscore": ["CheckScore", "checkscore"],
    "/reviews": ["Reviews", "reviews"],
    "/guestFormRequests": ["GuestFormRequests", "guest-form-requests"],
  };
  const match = guarded[normalizedPathname] || guarded["/"];
  return { name: match[0], view: match[1], params: {}, query };
}
